import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, User, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { VendorCard } from '@/components/VendorCard';
import { BlogMediaRenderer } from '@/components/BlogMediaRenderer';
import { toMetaDescription, upsertCanonical, upsertJsonLd, upsertMetaTag } from '@/lib/seoMeta';
import { prerenderData } from '@/generated/prerenderData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [linkedVendors, setLinkedVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  useEffect(() => {
    if (typeof window === 'undefined' || !post) return;

    const origin = window.location.origin;
    const canonicalUrl = `${origin}/blog/${post.slug || slug}`;
    const description = toMetaDescription(
      post.excerpt || post.content,
      'Read this Gibraltar Wedding Expo article for practical planning guidance and local supplier insight.'
    );

    document.title = `${post.title} | Gibraltar Wedding Expo Blog`;
    upsertMetaTag('name', 'description', description);
    upsertMetaTag('name', 'robots', 'index, follow');
    upsertCanonical(canonicalUrl);
    upsertMetaTag('property', 'og:title', `${post.title} | Gibraltar Wedding Expo`);
    upsertMetaTag('property', 'og:description', description);
    upsertMetaTag('property', 'og:url', canonicalUrl);
    upsertMetaTag('property', 'og:type', 'article');

    upsertJsonLd('blog-post-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description,
      datePublished: post.created_at,
      dateModified: post.updated_at || post.created_at,
      author: {
        '@type': 'Person',
        name: post.author_name || 'Gibraltar Wedding Expo',
      },
      mainEntityOfPage: canonicalUrl,
      image: post.featured_image || post.featured_media_url || undefined,
    });
  }, [post, slug]);

  const fetchPost = async () => {
    const fallbackPost = (prerenderData?.blogPosts || []).find(
      (candidate) => candidate?.slug === slug || candidate?.id === slug || candidate?._id === slug,
    );

    const fallbackLinkedVendors = (ids = []) => {
      if (!Array.isArray(ids) || ids.length === 0) return [];
      const vendors = prerenderData?.vendors || [];
      return ids
        .map((vendorId) => vendors.find((candidate) => candidate?.id === vendorId || candidate?._id === vendorId))
        .filter(Boolean);
    };

    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
      if (response.data.linked_vendor_ids?.length) {
        const vendorResponses = await Promise.all(
          response.data.linked_vendor_ids.map((vendorId) =>
            axios.get(`${API}/vendors/${vendorId}`).then((res) => res.data).catch(() => null)
          )
        );
        const resolvedVendors = vendorResponses.filter(Boolean);
        setLinkedVendors(
          resolvedVendors.length > 0
            ? resolvedVendors
            : fallbackLinkedVendors(response.data.linked_vendor_ids),
        );
      } else {
        setLinkedVendors([]);
      }
    } catch (error) {
      console.error('Failed to fetch blog post:', error);
      if (fallbackPost) {
        setPost(fallbackPost);
        setLinkedVendors(fallbackLinkedVendors(fallbackPost.linked_vendor_ids));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#5C0120]" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-stone-600 text-lg mb-4">Post not found</p>
        <Link to="/blog">
          <Button variant="outline" className="btn-outline">Back to Blog</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24" data-testid="blog-post-page">
      <article className="container-wedding max-w-4xl">
        {/* Back Button */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-stone-600 hover:text-[#5C0120] mb-8 transition-colors duration-200"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Blog
        </Link>

        {/* Header */}
        <header className="mb-8">
          <span className="caption text-[#D4AF37]">{post.category}</span>
          <h1 className="heading-xl mt-2 mb-4">{post.title}</h1>
          <div className="flex items-center gap-4 text-sm text-stone-500">
            <span className="flex items-center gap-2">
              <User className="h-4 w-4" />
              {post.author_name}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {format(new Date(post.created_at), 'MMMM d, yyyy')}
            </span>
          </div>
        </header>

        {(post.featured_media_url || post.featured_image || (post.gallery_images && post.gallery_images.length > 0) || post.video_url) && (
          <div className="mb-8 grid gap-4 lg:grid-cols-[1.35fr_0.95fr] lg:items-start">
            {(post.featured_media_url || post.featured_image || post.video_url) && (
              <div className="rounded-[24px] overflow-hidden border border-stone-100 bg-stone-50">
                <BlogMediaRenderer
                  mediaUrl={post.featured_media_url || post.video_url || post.featured_image}
                  mediaType={post.featured_media_type || (post.video_url ? 'video' : 'image')}
                  alt={post.title}
                  variant="hero"
                  imageClassName="w-full aspect-video object-cover"
                  videoClassName="max-h-[70vh] w-full object-contain"
                  testIdPrefix="blog-post-media"
                />
              </div>
            )}

            {post.gallery_images && post.gallery_images.length > 0 && (
              <div className="grid grid-cols-2 gap-3" data-testid="blog-post-gallery-grid">
                {post.gallery_images.map((image, index) => (
                  <div key={image} className="rounded-2xl overflow-hidden border border-stone-100 bg-stone-50">
                    <img src={image} alt={`${post.title} gallery ${index + 1}`} className="w-full aspect-square object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-medium prose-a:text-[#5C0120]"
          dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, '<br />') }}
        />

        {linkedVendors.length > 0 && (
          <section className="mt-12 border-t border-stone-100 pt-8" data-testid="blog-linked-vendors-section">
            <p className="caption mb-3">Related vendors</p>
            <h2 className="text-2xl font-semibold text-stone-900 mb-5">Explore vendors mentioned in this post</h2>
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {linkedVendors.map((vendor) => (
                <VendorCard key={vendor.id} vendor={vendor} />
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="mt-16 p-8 bg-[#5C0120]/5 rounded-sm text-center">
          <h3 className="heading-md mb-4">Ready to Start Planning?</h3>
          <p className="text-stone-600 mb-6">
            Browse our directory of trusted wedding vendors in Gibraltar.
          </p>
          <Link to="/vendors">
            <Button className="btn-primary">Explore Vendors</Button>
          </Link>
        </div>
      </article>
    </div>
  );
}
